import { Input } from "@/components/ui/input";
import { Search, ArrowUpDown } from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { AVAILABILITIES } from "@/lib/api";

const CONDITIONS = ["all", "Poor", "Good", "Excellent", "Near Mint", "Mint"];
const LOCATIONS = ["all", "Ruth", "Andy", "Archive", "Lagom"];
const SPEEDS = ["all", "33", "45", "78"];
const AVAILABILITY_OPTS = ["all", ...AVAILABILITIES];
const SORT_OPTIONS = [
    { label: "Recently added", value: "created_at:desc" },
    { label: "Oldest added", value: "created_at:asc" },
    { label: "Artist A→Z", value: "artist:asc" },
    { label: "Artist Z→A", value: "artist:desc" },
    { label: "Album A→Z", value: "album_title:asc" },
    { label: "Release year (new→old)", value: "release_year:desc" },
    { label: "Release year (old→new)", value: "release_year:asc" },
    { label: "Last cleaned", value: "date_last_cleaned:desc" },
];

export default function FiltersBar({ search, setSearch, filters, setFilters, sort, setSort, genres }) {
    const set = (k, v) => setFilters({ ...filters, [k]: v });
    const sortValue = `${sort.field}:${sort.order}`;

    return (
        <section className="space-y-4" data-testid="filters-bar">
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by artist or album title…"
                    className="pl-11 h-12 bg-card border-border rounded-md text-base"
                    data-testid="search-input"
                />
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <Select value={filters.location} onValueChange={(v) => set("location", v)}>
                    <SelectTrigger className="w-[140px] bg-card" data-testid="filter-location">
                        <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                        {LOCATIONS.map(o => (
                            <SelectItem key={o} value={o} data-testid={`filter-location-${o}`}>
                                {o === "all" ? "All locations" : o}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={filters.media_condition} onValueChange={(v) => set("media_condition", v)}>
                    <SelectTrigger className="w-[180px] bg-card" data-testid="filter-media">
                        <SelectValue placeholder="Media condition" />
                    </SelectTrigger>
                    <SelectContent>
                        {CONDITIONS.map(c => (
                            <SelectItem key={c} value={c}>
                                {c === "all" ? "Any media cond." : `Media: ${c}`}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={filters.availability || "all"} onValueChange={(v) => set("availability", v)}>
                    <SelectTrigger className="w-[160px] bg-card" data-testid="filter-availability">
                        <SelectValue placeholder="Availability" />
                    </SelectTrigger>
                    <SelectContent>
                        {AVAILABILITY_OPTS.map(a => (
                            <SelectItem key={a} value={a} data-testid={`filter-availability-${a}`}>
                                {a === "all" ? "Any availability" : a}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={filters.speed} onValueChange={(v) => set("speed", v)}>
                    <SelectTrigger className="w-[120px] bg-card" data-testid="filter-speed">
                        <SelectValue placeholder="Speed" />
                    </SelectTrigger>
                    <SelectContent>
                        {SPEEDS.map(s => (
                            <SelectItem key={s} value={s}>
                                {s === "all" ? "Any speed" : `${s} rpm`}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {genres.length > 0 && (
                    <Select value={filters.genre} onValueChange={(v) => set("genre", v)}>
                        <SelectTrigger className="w-[160px] bg-card" data-testid="filter-genre">
                            <SelectValue placeholder="Genre" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All genres</SelectItem>
                            {genres.map(g => (
                                <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <div className="ml-auto flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                    <Select
                        value={sortValue}
                        onValueChange={(v) => {
                            const [field, order] = v.split(":");
                            setSort({ field, order });
                        }}
                    >
                        <SelectTrigger className="w-[220px] bg-card" data-testid="sort-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SORT_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </section>
    );
}
